import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { leadService } from '../../services/leadService';
import { oportunidadService } from '../../services/oportunidadService';
import { actividadService } from '../../services/actividadService';

const CRMDashboard = () => {
  const navigate = useNavigate();

  const { data: leadsData } = useQuery(
    'leads',
    () => leadService.getAll(1, 1000)
  );

  const { data: oportunidadesData } = useQuery(
    'oportunidades',
    () => oportunidadService.getAll(1, 1000)
  );

  const { data: etapasData } = useQuery(
    'pipeline-etapas',
    () => oportunidadService.getPipelineEtapas()
  );

  const { data: actividadesData } = useQuery(
    'actividades-recientes',
    () => actividadService.getAll(1, 10)
  );

  const leads = leadsData?.data.leads || [];
  const oportunidades = oportunidadesData?.data.oportunidades || [];
  const actividades = actividadesData?.data.actividades || [];
  const etapas = etapasData?.data.etapas || [];

  // Calcular métricas
  const totalLeads = leads.length;
  const leadsNuevos = leads.filter(l => l.estado === 'nuevo').length;
  const leadsContactados = leads.filter(l => l.estado === 'contactado').length;
  const leadsCalificados = leads.filter(l => l.estado === 'calificado').length;
  
  const totalOportunidades = oportunidades.length;
  const oportunidadesActivas = oportunidades.filter(o => o.estado === 'activa').length;
  const valorPipeline = oportunidades
    .filter(o => o.estado === 'activa')
    .reduce((sum, o) => sum + (typeof o.valor === 'string' ? parseFloat(o.valor) : (o.valor || 0)), 0);

  // Calcular métricas por etapa del pipeline
  const oportunidadesPorEtapa = etapas.map(etapa => {
    const opsEnEtapa = oportunidades.filter(o => o.etapaId === etapa.id && o.estado === 'activa');
    const valorEnEtapa = opsEnEtapa.reduce((sum, o) => {
      const valor = typeof o.valor === 'string' ? parseFloat(o.valor) : (o.valor || 0);
      return sum + valor;
    }, 0);
    return {
      etapa,
      cantidad: opsEnEtapa.length,
      valor: valorEnEtapa
    };
  });

  // Mantener métricas por estado para otras secciones
  const oportunidadesGanadas = oportunidades.filter(o => o.estado === 'ganada').length;
  const valorGanado = oportunidades
    .filter(o => o.estado === 'ganada')
    .reduce((sum, o) => sum + (typeof o.valor === 'string' ? parseFloat(o.valor) : (o.valor || 0)), 0);
  
  const oportunidadesPerdidas = oportunidades.filter(o => o.estado === 'perdida').length;
  
  const tasaConversion = totalOportunidades > 0
    ? ((oportunidadesGanadas / totalOportunidades) * 100).toFixed(1)
    : '0.0';
  
  // Actividades del día
  const hoy = new Date();
  const actividadesHoy = actividades.filter(a => {
    if (!a.createdAt) return false;
    const fechaActividad = new Date(a.createdAt);
    return fechaActividad.toDateString() === hoy.toDateString();
  }).length;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Dashboard CRM</h1>
          <p className="text-gray-600 mt-1">Vista general de leads, oportunidades y actividades</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => navigate('/crm/leads/nuevo')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            + Nuevo Lead
          </button>
          <button
            onClick={() => navigate('/crm/oportunidades/nueva')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            + Nueva Oportunidad
          </button>
        </div>
      </div>

      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Leads</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">{totalLeads}</p>
              <p className="text-xs text-gray-500 mt-1">
                {leadsNuevos} nuevos • {leadsContactados} contactados • {leadsCalificados} calificados
              </p>
            </div>
            <div className="bg-blue-100 rounded-full p-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Oportunidades Activas</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{oportunidadesActivas}</p>
              <p className="text-xs text-gray-500 mt-1">
                {oportunidadesGanadas} ganadas • {oportunidadesPerdidas} perdidas
              </p>
            </div>
            <div className="bg-green-100 rounded-full p-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Valor del Pipeline</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">
                ${valorPipeline.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Ganado: ${valorGanado.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
            <div className="bg-purple-100 rounded-full p-4">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Tasa de Conversión</p>
              <p className="text-3xl font-bold text-orange-600 mt-2">{tasaConversion}%</p>
              <p className="text-xs text-gray-500 mt-1">
                {actividadesHoy} actividades hoy
              </p>
            </div>
            <div className="bg-orange-100 rounded-full p-4">
              <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Indicadores por Etapa del Pipeline */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Oportunidades por Etapa del Pipeline</h2>
          <button
            onClick={() => navigate('/crm/oportunidades')}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            Ver Pipeline →
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {oportunidadesPorEtapa
            .filter(item => item.etapa.isActive)
            .sort((a, b) => a.etapa.orden - b.etapa.orden)
            .map((item) => (
              <div
                key={item.etapa.id}
                className="border-l-4 rounded-lg p-4 transition hover:shadow-md"
                style={{
                  borderLeftColor: item.etapa.color || '#3B82F6',
                  backgroundColor: `${item.etapa.color || '#3B82F6'}10`
                }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">{item.etapa.nombre}</p>
                    <p className="text-2xl font-bold mt-1" style={{ color: item.etapa.color || '#3B82F6' }}>
                      {item.cantidad}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      ${item.valor.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </p>
                    {item.etapa.probabilidadDefault > 0 && (
                      <p className="text-xs text-gray-400 mt-1">
                        {item.etapa.probabilidadDefault}% prob. default
                      </p>
                    )}
                  </div>
                  <div
                    className="rounded-full p-3"
                    style={{ backgroundColor: `${item.etapa.color || '#3B82F6'}20` }}
                  >
                    <svg className="w-6 h-6" style={{ color: item.etapa.color || '#3B82F6' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
        </div>
        {oportunidadesPorEtapa.length === 0 && (
          <p className="text-gray-500 text-center py-4">No hay etapas configuradas en el pipeline</p>
        )}
      </div>

      {/* Secciones principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads recientes */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Leads Recientes</h2>
            <button
              onClick={() => navigate('/crm/leads')}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              Ver todos →
            </button>
          </div>
          <div className="space-y-3">
            {leads.slice(0, 5).map((lead) => (
              <div
                key={lead.id}
                onClick={() => navigate(`/crm/leads/${lead.id}`)}
                className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{lead.nombre}</p>
                    {lead.empresa && (
                      <p className="text-sm text-gray-500">{lead.empresa}</p>
                    )}
                    {lead.email && (
                      <p className="text-xs text-gray-400">{lead.email}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    lead.estado === 'nuevo' ? 'bg-blue-100 text-blue-800' :
                    lead.estado === 'contactado' ? 'bg-yellow-100 text-yellow-800' :
                    lead.estado === 'calificado' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {lead.estado}
                  </span>
                </div>
              </div>
            ))}
            {leads.length === 0 && (
              <p className="text-gray-500 text-center py-4">No hay leads registrados</p>
            )}
          </div>
        </div>

        {/* Oportunidades recientes */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Oportunidades Recientes</h2>
            <button
              onClick={() => navigate('/crm/oportunidades')}
              className="text-primary-600 hover:text-primary-700 text-sm font-medium"
            >
              Ver todas →
            </button>
          </div>
          <div className="space-y-3">
            {oportunidades.slice(0, 5).map((oportunidad) => (
              <div
                key={oportunidad.id}
                onClick={() => navigate(`/crm/oportunidades/${oportunidad.id}`)}
                className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{oportunidad.nombre}</p>
                    {oportunidad.cliente && (
                      <p className="text-sm text-gray-500">{oportunidad.cliente.nombre}</p>
                    )}
                    <p className="text-sm font-medium text-green-600">
                      ${(typeof oportunidad.valor === 'string' ? parseFloat(oportunidad.valor) : (oportunidad.valor || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    oportunidad.etapa?.color ? `bg-[${oportunidad.etapa.color}]20` : 'bg-gray-100'
                  } text-gray-800`}>
                    {oportunidad.etapa?.nombre || 'Sin etapa'}
                  </span>
                </div>
              </div>
            ))}
            {oportunidades.length === 0 && (
              <p className="text-gray-500 text-center py-4">No hay oportunidades registradas</p>
            )}
          </div>
        </div>
      </div>

      {/* Actividades recientes */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Actividades Recientes</h2>
          <button
            onClick={() => navigate('/crm/actividades')}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            Ver todas →
          </button>
        </div>
        <div className="space-y-3">
          {actividades.map((actividad) => (
            <div key={actividad.id} className="p-3 border rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-gray-900">{actividad.titulo}</p>
                  <p className="text-sm text-gray-500">{actividad.descripcion}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {actividad.usuarioCreacion?.name} • {new Date(actividad.createdAt || '').toLocaleDateString('es-MX')}
                  </p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  actividad.tipo === 'llamada' ? 'bg-blue-100 text-blue-800' :
                  actividad.tipo === 'visita' ? 'bg-green-100 text-green-800' :
                  actividad.tipo === 'correo' ? 'bg-indigo-100 text-indigo-800' :
                  actividad.tipo === 'whatsapp' ? 'bg-emerald-100 text-emerald-800' :
                  actividad.tipo === 'reunion' ? 'bg-purple-100 text-purple-800' :
                  actividad.tipo === 'tarea' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {actividad.tipo}
                </span>
              </div>
            </div>
          ))}
          {actividades.length === 0 && (
            <p className="text-gray-500 text-center py-4">No hay actividades recientes</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CRMDashboard;

